/* eslint-disable complexity */
/* eslint-disable react/prop-types */
import React, { useMemo, useState, useEffect } from 'react';
import { CircularProgress, Alert } from '@mui/material';
import { IPaymentMethods } from '../../../modules/common/models/orderData';
import { isGroup, hasId } from '../../../modules/order/utils/paymentMethods';

interface PaymentMethodsListProps {
  paymentMethods: IPaymentMethods[];
  paymentStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  paymentError?: string | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
  t: (key: string, defaultValue?: string) => string;
}

const compactCard = (count: number) => count > 8;

const PaymentMethodsList: React.FC<PaymentMethodsListProps> = ({
  paymentMethods,
  paymentStatus,
  paymentError,
  selectedId,
  onSelect,
  t,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (selectedId == null) {
      const firstPriorityLeaf = paymentMethods.find(
        (m) => m.priority && !isGroup(m) && hasId(m)
      );
      if (firstPriorityLeaf && hasId(firstPriorityLeaf)) {
        onSelect(firstPriorityLeaf.id);
      }
    }
  }, [selectedId, paymentMethods, onSelect]);

  const { priorityItems, nonPriorityGroups } = useMemo(() => {
    const pri = paymentMethods.filter((m) => m.priority);
    const groupsBelow = paymentMethods.filter((m) => isGroup(m) && !m.priority);
    return { priorityItems: pri, nonPriorityGroups: groupsBelow };
  }, [paymentMethods]);

  if (paymentStatus === 'loading') return <CircularProgress size={24} />;
  if (paymentStatus === 'failed') {
    return (
      <Alert severity="error">
        {paymentError || t('common:error_message')}
      </Alert>
    );
  }

  const toggleGroup = (key: string) =>
    setExpandedGroups((s) => ({ ...s, [key]: !s[key] }));

  const MethodCard: React.FC<{ m: IPaymentMethods; compact?: boolean }> = ({
    m,
    compact,
  }) => {
    const isActive = hasId(m) && selectedId === m.id;
    return (
      <button
        type="button"
        className={`payment-method-card ${isActive ? 'is-active' : ''} ${
          compact ? 'payment-method-card--compact' : ''
        }`}
        onClick={() => hasId(m) && onSelect(m.id)}
        aria-pressed={isActive}
        disabled={!hasId(m)}
      >
        <div className="payment-method-card__left">
          {m.iconUrl ? (
            <img
              src={m.iconUrl}
              alt={m.name}
              className="payment-method-card__icon"
              loading="lazy"
            />
          ) : (
            <div className="payment-method-card__icon--placeholder" />
          )}
        </div>
        <div className="payment-method-card__right">
          <div className="payment-method-card__name">{m.name}</div>
          {!!m.shortDescription && (
            <div className="payment-method-card__desc">
              {m.shortDescription}
            </div>
          )}
        </div>
      </button>
    );
  };

  const GroupCard: React.FC<{
    g: IPaymentMethods;
    emphasize?: boolean;
    inGrid?: boolean;
  }> = ({ g, emphasize, inGrid }) => {
    const key = g.name || String(g.id ?? '');
    const expanded = !!expandedGroups[key];
    const children = g.paymentMethods ?? [];
    const useCompact = compactCard(children.length);

    const headerBtn = (
      <button
        type="button"
        className={`payment-method-card payment-method-card--group ${
          expanded ? 'is-open' : ''
        } ${emphasize ? 'payment-method-card--emphasize' : ''}`}
        onClick={() => toggleGroup(key)}
        aria-expanded={expanded}
      >
        <div className="payment-method-card__left">
          {g.iconUrl ? (
            <img
              src={g.iconUrl}
              alt={g.name}
              className="payment-method-card__icon"
              loading="lazy"
            />
          ) : (
            <div className="payment-method-card__icon--placeholder" />
          )}
        </div>
        <div className="payment-method-card__right">
          <div className="payment-method-card__name">{g.name}</div>
          {!!g.shortDescription && (
            <div className="payment-method-card__desc">
              {g.shortDescription}
            </div>
          )}
        </div>
        <div className="payment-method-card__chevron" aria-hidden>
          ▾
        </div>
      </button>
    );

    if (inGrid) {
      return (
        <>
          {headerBtn}
          {expanded && children.length > 0 && (
            <div
              className={`payment-group__children payment-methods-grid ${
                useCompact ? 'payment-methods-grid--compact' : ''
              }`}
            >
              {children.map((m) => (
                <MethodCard
                  key={`${key}-${m.id ?? m.name}`}
                  m={m}
                  compact={useCompact}
                />
              ))}
            </div>
          )}
        </>
      );
    }

    return (
      <div className={`payment-group ${emphasize ? 'is-emphasize' : ''}`}>
        {headerBtn}
        {expanded && children.length > 0 && (
          <div
            className={`payment-methods-grid ${
              useCompact ? 'payment-methods-grid--compact' : ''
            }`}
          >
            {children.map((m) => (
              <MethodCard
                key={`${key}-${m.id ?? m.name}`}
                m={m}
                compact={useCompact}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {priorityItems.length > 0 && (
        <>
          <div className="text-start font-semibold my-4">
            {t('pages:cart_summary.select_payment_method')}
          </div>

          <div className="payment-methods-grid">
            {priorityItems.map((item) =>
              isGroup(item) ? (
                <GroupCard
                  key={`pri-${item.name}-${item.id}`}
                  g={item}
                  emphasize
                  inGrid
                />
              ) : (
                <MethodCard key={`pri-${item.id}`} m={item} />
              )
            )}
          </div>
        </>
      )}

      {nonPriorityGroups.length > 0 && (
        <>
          <div className="text-start font-semibold mt-6 mb-3">
            {t('pages:cart_summary.other_payment_groups')}
          </div>

          <div className="payment-groups-stack">
            {nonPriorityGroups.map((g) => (
              <GroupCard key={`grp-${g.name}-${g.id}`} g={g} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentMethodsList;
